import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { DEFAULTS, MEDIA_DEFAULTS } from "@/content/defaults";

const AppContext = createContext(null);

const deepMerge = (base, override) => {
  if (!override || typeof override !== "object") return base;
  const out = Array.isArray(base) ? [...base] : { ...base };
  for (const key of Object.keys(override)) {
    const ov = override[key];
    if (ov && typeof ov === "object" && !Array.isArray(ov) && base && typeof base[key] === "object") {
      out[key] = deepMerge(base[key], ov);
    } else {
      out[key] = ov;
    }
  }
  return out;
};

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("agrivoire_lang") || "fr");
  const [overrides, setOverrides] = useState({});
  const [loaded, setLoaded] = useState(false);

  const fetchContent = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setOverrides(data.data || {});
    } catch (e) {
      setOverrides({});
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  useEffect(() => {
    localStorage.setItem("agrivoire_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const content = deepMerge(DEFAULTS, overrides);
  const media = deepMerge(MEDIA_DEFAULTS, overrides.media || {});

  // translate: t(page, key) -> string in current language
  const t = useCallback((page, key) => {
    const node = content?.[page]?.[key];
    if (!node) return "";
    return node[lang] ?? node.fr ?? "";
  }, [content, lang]);

  const m = useCallback((slot) => media?.[slot] || "", [media]);

  const toggleLang = () => setLang((l) => (l === "fr" ? "en" : "fr"));

  return (
    <AppContext.Provider value={{ lang, setLang, toggleLang, t, m, content, media, DEFAULTS, refreshContent: fetchContent, loaded }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

// UI strings that are not editable via CMS
export const UI = {
  fr: {
    lang_switch: "EN",
    read_more: "En savoir plus",
    send_request: "Envoyer ma demande",
    book_meeting: "Prendre rendez-vous",
    full_name: "Nom complet", company: "Entreprise / Coopérative", organization: "Entreprise / Organisation",
    job_title: "Poste / Fonction",
    attestation: "Je certifie que les informations renseignées dans ce formulaire sont exactes et que je suis la personne identifiée ci-dessus, habilitée à les communiquer au nom de l'entité mentionnée.",
    attestation_error: "Vous devez certifier l'exactitude des informations pour continuer",
    country: "Pays", email: "Email", phone: "Téléphone (optionnel)",
    i_am: "Je suis :", role_buyer: "Acheteur", role_supplier: "Fournisseur / Négociant (j'achète auprès de producteurs et je revends)", role_producer: "Producteur (agriculteur, coopérative agricole)",
    products: "Produit(s) concerné(s) :", volume: "Volume / fréquence estimés (optionnel)",
    message: "Message", project_desc: "Description du projet",
    mission_type: "Type de mission souhaitée :",
    mission_market: "Entrée sur le marché ivoirien / ouest-africain",
    mission_export: "Accompagnement à l'export",
    mission_deals: "Structuration d'accords commerciaux",
    mission_watch: "Veille réglementaire", mission_other: "Autre",
    required: "Ce champ est requis", invalid_email: "Email invalide",
    select_one: "Sélectionnez une option", pick_products: "Sélectionnez au moins un produit",
    security_q: "Question de sécurité", math_wrong: "Réponse incorrecte",
    sending: "Envoi en cours...", success: "Votre demande a bien été envoyée. Merci !",
    error: "Une erreur est survenue. Veuillez réessayer.",
    prod_cacao: "Cacao", prod_fruits: "Fruits exotiques", prod_cajou: "Noix de cajou",
    prod_cafe: "Café",
    prod_caoutchouc: "Caoutchouc", prod_kola: "Noix de kola",
    prod_autre: "Autres",
    product_other_ph: "Ex : Igname, Karité, Hévéa transformé",
    product_other_invalid: "Veuillez saisir un nom de produit valide (au moins 2 lettres)",
    product_other_hint: "Veuillez préciser le produit ci-dessus pour continuer.",
    cert_section_title: "Statut de certification",
    cert_obtained_label: "Certification(s) obtenue(s) :",
    cert_globalgap: "GlobalGAP", cert_fairtrade: "Fairtrade",
    cert_rainforest: "Rainforest Alliance / UTZ", cert_bio: "Bio",
    cert_autre: "Autre",
    cert_other_ph: "Précisez la certification",
    cert_status_label: "Ou indiquez votre situation :",
    cert_in_progress: "Certification en cours",
    cert_none: "Non certifié",
    cert_numbers_label: "Numéro(s) de certificat (si applicable)",
    support_label: "Souhaite un accompagnement à la certification",
    msg_eudr: "L'export de {products} vers l'Europe est désormais soumis à la réglementation européenne anti-déforestation (EUDR). Une traçabilité complète et une certification adaptée sont indispensables pour accéder au marché européen. Notre équipe peut vous accompagner dans cette démarche.",
    msg_standard: "Les acheteurs internationaux exigent de plus en plus la certification pour l'export de {products}. Obtenir une certification reconnue renforce votre accès aux marchés et valorise vos produits. Notre équipe peut vous accompagner dans cette démarche.",
    play_video: "Lire la vidéo",
    label_company: "Entreprise", label_location: "Localisation", label_email: "Email", label_phone: "Téléphone",
  },
  en: {
    lang_switch: "FR",
    read_more: "Learn more",
    send_request: "Send my request",
    book_meeting: "Book a meeting",
    full_name: "Full name", company: "Company / Cooperative", organization: "Company / Organization",
    job_title: "Job title / Role",
    attestation: "I certify that the information provided in this form is accurate and that I am the person identified above, duly authorized to submit it on behalf of the stated entity.",
    attestation_error: "You must certify the accuracy of the information to continue",
    country: "Country", email: "Email", phone: "Phone (optional)",
    i_am: "I am:", role_buyer: "Buyer", role_supplier: "Supplier / Trader (I purchase from producers and resell)", role_producer: "Producer (farmer, agricultural cooperative)",
    products: "Product(s) concerned:", volume: "Estimated volume / frequency (optional)",
    message: "Message", project_desc: "Project description",
    mission_type: "Type of mission requested:",
    mission_market: "Entry into the Ivorian / West African market",
    mission_export: "Export support",
    mission_deals: "Structuring trade agreements",
    mission_watch: "Regulatory watch", mission_other: "Other",
    required: "This field is required", invalid_email: "Invalid email",
    select_one: "Select an option", pick_products: "Select at least one product",
    security_q: "Security question", math_wrong: "Incorrect answer",
    sending: "Sending...", success: "Your request has been sent. Thank you!",
    error: "An error occurred. Please try again.",
    prod_cacao: "Cocoa", prod_fruits: "Exotic fruits", prod_cajou: "Cashew nuts",
    prod_cafe: "Coffee",
    prod_caoutchouc: "Rubber", prod_kola: "Kola nut",
    prod_autre: "Other",
    product_other_ph: "E.g.: Yam, Shea butter, Processed rubber",
    product_other_invalid: "Please enter a valid product name (at least 2 letters)",
    product_other_hint: "Please specify the product above to continue.",
    cert_section_title: "Certification status",
    cert_obtained_label: "Certification(s) held:",
    cert_globalgap: "GlobalGAP", cert_fairtrade: "Fairtrade",
    cert_rainforest: "Rainforest Alliance / UTZ", cert_bio: "Organic",
    cert_autre: "Other",
    cert_other_ph: "Specify the certification",
    cert_status_label: "Or indicate your situation:",
    cert_in_progress: "Certification in progress",
    cert_none: "Not certified",
    cert_numbers_label: "Certificate number(s) (if applicable)",
    support_label: "Interested in certification support / guidance",
    msg_eudr: "The export of {products} to Europe is now subject to the European anti-deforestation regulation (EUDR). Full traceability and appropriate certification are essential to access the European market. Our team can support you throughout this process.",
    msg_standard: "International buyers increasingly require certification for the export of {products}. Obtaining a recognized certification strengthens your market access and adds value to your products. Our team can support you throughout this process.",
    play_video: "Play video",
    label_company: "Company", label_location: "Location", label_email: "Email", label_phone: "Phone",
  },
};

export const useUI = () => {
  const { lang } = useApp();
  return UI[lang];
};
